import React, { useState, useMemo, useRef, useCallback, startTransition, memo } from 'react';
import PropTypes from 'prop-types';
import OmDocumentUpload from '../ui/OmDocumentUpload';

/**
 * Modal Document Upload Component
 * Based on Step4DocumentUpload but simplified for modal use case
 * Removes navigation buttons and form wrapper
 */
const ModalDocumentUpload = ({
  formData,
  errors = {},
  validationErrors = {},
  uploadedFiles = [],
  isLoading = false,
  onFileUpload,
  onFileRemove,
  schema = {},
  showError = () => {},
  onUploadSuccess
}) => {
  // Combined state for document type selection to reduce re-renders
  const [documentTypeState, setDocumentTypeState] = useState({
    selectedDocumentType: '',
    customDocumentType: '',
    showCustomInput: false
  });
  
  // Refs to prevent React reconciliation issues
  const fileInputRef = useRef(null);
  
  // Debounced state update function to batch multiple state changes
  const updateDocumentTypeState = useCallback((updates) => {
    startTransition(() => {
      setDocumentTypeState(prev => ({
        ...prev,
        ...updates
      }));
    });
  }, []);
  
  // Clear all document type state in one batched update
  const clearDocumentTypeState = useCallback(() => {
    updateDocumentTypeState({
      selectedDocumentType: '',
      customDocumentType: '',
      showCustomInput: false
    });
  }, [updateDocumentTypeState]);
  
  // Extract values for easier use
  const { selectedDocumentType, customDocumentType, showCustomInput } = documentTypeState;

  // Default required documents by claim type
  const getDefaultRequiredDocuments = useCallback(() => ({
    'DTH': ['DEATH_CERTIFICATE', 'ID', 'RELATIONSHIP_DECLARATION'],
    '010': ['DEATH_CERTIFICATE', 'ID', 'RELATIONSHIP_DECLARATION', 'MARRIAGE_CERTIFICATE'],
    '012': ['DEATH_CERTIFICATE', 'ID', 'FUNERAL_RECEIPTS', 'BURIAL_CERTIFICATE'],
    '013': ['DEATH_CERTIFICATE', 'ID', 'RELATIONSHIP_DECLARATION', 'BIRTH_CERTIFICATE'],
    '002': ['CRITICAL_ILLNESS_DIAGNOSIS', 'ID', 'MEDICAL_REPORT'],
    '004': ['MEDICAL_REPORT', 'ID', 'EMPLOYER_CONFIRMATION', 'DISABILITY_ASSESSMENT'],
    '005': ['MEDICAL_REPORT', 'ID', 'INVOICE', 'TREATMENT_PLAN'],
    '006': ['MEDICAL_REPORT', 'ID', 'EMPLOYER_CONFIRMATION', 'EMPLOYMENT_LETTER'],
    // Legacy/fallback mappings
    'DEATH': ['DEATH_CERTIFICATE', 'ID', 'RELATIONSHIP_DECLARATION'],
    'DISABILITY': ['MEDICAL_REPORT', 'ID', 'EMPLOYER_CONFIRMATION', 'DISABILITY_ASSESSMENT'],
    'CRITICAL_ILLNESS': ['CRITICAL_ILLNESS_DIAGNOSIS', 'ID', 'MEDICAL_REPORT'],
    'MEDICAL': ['MEDICAL_REPORT', 'ID', 'INVOICE']
  }), []);

  // Map descriptive claim types to business codes
  const mapClaimTypeToBusinessCode = useCallback((claimType) => {
    const mappings = {
      'DEATH': 'DTH',
      'SPOUSE_DEATH': '010',
      'FUNERAL_EXPENSES': '012',
      'CHILD_DEATH': '013',
      'CRITICAL_ILLNESS': '002',
      'DISABILITY': '004',
      'MEDICAL_EXPENSES': '005',
      'TEMPORARY_DISABILITY': '006'
    };
    
    return mappings[claimType] || claimType;
  }, []);

  // Get required documents for the current claim type
  const getRequiredDocuments = useCallback(() => {
    const rawClaimType = formData?.type || formData?.claimType;
    
    if (!rawClaimType) {
      console.log('⚠️ ModalDocumentUpload: No claim type found in formData:', formData);
      return [];
    }

    // Map to business code if needed
    const claimType = mapClaimTypeToBusinessCode(rawClaimType);
    
    // First try to get from schema
    if (schema?.requiredDocuments) {
      const required = schema.requiredDocuments[claimType] || [];
      if (required.length > 0) {
        console.log('✅ ModalDocumentUpload: Found required documents from schema for', claimType, ':', required);
        return required;
      }
    }
    
    // Fallback to default required documents
    const defaultRequired = getDefaultRequiredDocuments();
    const required = defaultRequired[claimType] || [];
    
    console.log(`📋 ModalDocumentUpload: Using ${required.length > 0 ? 'default' : 'no'} required documents for claim type "${rawClaimType}" (mapped to "${claimType}"):`, required);
    return required;
  }, [schema?.requiredDocuments, formData, getDefaultRequiredDocuments, mapClaimTypeToBusinessCode]);

  // Default document types if schema is not provided
  const getDefaultDocumentTypes = useCallback(() => [
    { value: 'CLAIM_FORM', label: 'Claim Form' },
    { value: 'MEDICAL_CERTIFICATE', label: 'Medical Certificate' },
    { value: 'EMPLOYER_CONFIRMATION', label: 'Employer Confirmation' },
    { value: 'RELATIONSHIP_DECLARATION', label: 'Relationship Declaration' },
    { value: 'BANK_DETAILS', label: 'Bank Details' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'BURIAL_CERTIFICATE', label: 'Burial Certificate' },
    { value: 'CRITICAL_ILLNESS_DIAGNOSIS', label: 'Critical Illness Diagnosis' },
    { value: 'TREATMENT_PLAN', label: 'Treatment Plan' },
    { value: 'MEDICAL_REPORT', label: 'Medical Report' },
    { value: 'DEATH_CERTIFICATE', label: 'Death Certificate' },
    { value: 'ID', label: 'ID Document' },
    { value: 'BANK_STATEMENT', label: 'Bank Statement' },
    { value: 'POLICE_REPORT', label: 'Police Report' },
    { value: 'HOSPITAL_DISCHARGE', label: 'Hospital Discharge' },
    { value: 'PATHOLOGY_REPORT', label: 'Pathology Report' },
    { value: 'RADIOLOGY_REPORT', label: 'Radiology Report' },
    { value: 'DISABILITY_ASSESSMENT', label: 'Disability Assessment' },
    { value: 'EMPLOYMENT_LETTER', label: 'Employment Letter' },
    { value: 'OTHER', label: 'Other' }
  ], []);

  // Get document type options for dropdown
  const getDocumentTypeOptions = useCallback(() => {
    const documentTypes = schema?.documentTypes || getDefaultDocumentTypes();
    
    const required = getRequiredDocuments();
    const requiredOptions = documentTypes.filter(doc => 
      required.includes(doc.value)
    );
    const otherOption = documentTypes.find(doc => doc.value === 'OTHER');
    
    return [...requiredOptions, ...(otherOption ? [otherOption] : [])];
  }, [schema?.documentTypes, getRequiredDocuments, getDefaultDocumentTypes]);

  // Calculate document completion percentage
  const documentProgress = useMemo(() => {
    const required = getRequiredDocuments();
    // Handle both 'type' (from backend) and 'documentType' (from upload)
    const uploadedDocTypes = uploadedFiles
      .filter(f => {
        const docType = f.documentType || f.type;
        return docType && docType !== 'OTHER';
      })
      .map(f => f.documentType || f.type);
    
    const completed = required.filter(doc => uploadedDocTypes.includes(doc));
    
    return {
      total: required.length,
      completed: completed.length,
      missing: required.filter(doc => !uploadedDocTypes.includes(doc)),
      percentage: required.length > 0 ? Math.round((completed.length / required.length) * 100) : 100
    };
  }, [getRequiredDocuments, uploadedFiles]);

  // Get document type label from value
  const getDocumentTypeLabel = useCallback((value) => {
    // Handle null/undefined values
    if (!value) return 'Unknown Document';
    
    const documentTypes = schema?.documentTypes || getDefaultDocumentTypes();
    const docType = documentTypes.find(doc => doc.value === value);
    return docType ? docType.label : value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, [schema?.documentTypes, getDefaultDocumentTypes]);
  
  // Generate structured filename
  const generateFileName = useCallback((originalName, documentType, isOther = false, userSpecifiedType = '') => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const extension = originalName.split('.').pop();
    const baseName = originalName.replace(/\.[^/.]+$/, '');
    
    let prefix = '';
    if (documentType && documentType !== 'OTHER') {
      prefix = `${documentType}_`;
    } else if (isOther && userSpecifiedType) {
      const sanitizedType = userSpecifiedType.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
      prefix = `${sanitizedType}_`;
    }
    
    return `${prefix}${baseName}_${timestamp}.${extension}`;
  }, []);

  // Handle document type change
  const handleDocumentTypeChange = useCallback((e) => {
    const value = e.target.value;
    const isOther = value === 'OTHER';
    updateDocumentTypeState({
      selectedDocumentType: value,
      showCustomInput: isOther,
      customDocumentType: isOther ? customDocumentType : ''
    });
  }, [customDocumentType, updateDocumentTypeState]);

  // Handle custom document type change
  const handleCustomDocumentTypeChange = useCallback((value) => {
    updateDocumentTypeState({
      customDocumentType: value
    });
  }, [updateDocumentTypeState]);

  // Enhanced file upload handler with structured naming
  const handleFileUpload = useCallback(async (files) => {
    if (!selectedDocumentType) {
      showError('Please select a document type before uploading files.');
      return;
    }

    if (selectedDocumentType === 'OTHER' && !customDocumentType.trim()) {
      showError('Please specify the custom document type before uploading files.');
      return;
    }

    try {
        const filesWithMetadata = Array.from(files).map(file => {
        const isOther = selectedDocumentType === 'OTHER';
        const structuredName = generateFileName(file.name, selectedDocumentType, isOther, customDocumentType);
        const isRequired = !isOther && getRequiredDocuments().includes(selectedDocumentType);
        
        return {
          file,
          documentType: selectedDocumentType,
          customDocumentType: isOther ? customDocumentType.trim() : null,
          structuredName,
          originalName: file.name,
          isRequired
        };
      });      await onFileUpload(filesWithMetadata);
      
      // Clear the document type selection after successful upload
      clearDocumentTypeState();
      
      // Call success callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('File upload error:', error);
      showError(error.message || 'Failed to upload files. Please try again.');
    }
  }, [selectedDocumentType, customDocumentType, generateFileName, onFileUpload, clearDocumentTypeState, showError, onUploadSuccess]);

  // Handle file removal
  const handleFileRemove = useCallback(async (fileId) => {
    try {
      await onFileRemove(fileId);
    } catch (error) {
      console.error('File removal error:', error);
      showError(error.message || 'Failed to remove file. Please try again.');
    }
  }, [onFileRemove, showError]);

  const documentTypeOptions = getDocumentTypeOptions();
  const uploadProgress = getUploadProgress;
  
  // Debug logging
  console.log('🔍 ModalDocumentUpload render:', {
    formData,
    claimType: formData?.type || formData?.claimType,
    requiredDocs: getRequiredDocuments(),
    schemaProvided: !!schema?.requiredDocuments,
    uploadedFiles: uploadedFiles?.length || 0
  });
  
  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      {uploadProgress.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-blue-900">Document Upload Progress</h3>
            <span className="text-sm font-medium text-blue-700">
              {uploadProgress.completed}/{uploadProgress.total} Required Documents
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress.percentage}%` }}
            />
          </div>
          {uploadProgress.missing.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-blue-700">
                Missing: {uploadProgress.missing.map(doc => getDocumentTypeLabel(doc)).join(', ')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Document Type Selection */}
      <div className="space-y-4">
        <div>
          <label htmlFor="documentType" className="block text-sm font-medium text-gray-700 mb-2">
            Document Type *
          </label>
          <select
            id="documentType"
            value={selectedDocumentType}
            onChange={handleDocumentTypeChange}
            disabled={isLoading}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select document type</option>
            {documentTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
                {getRequiredDocuments().includes(option.value) && ' (Required)'}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Document Type Input */}
        {showCustomInput && (
          <div>
            <label htmlFor="customDocumentType" className="block text-sm font-medium text-gray-700 mb-2">
              Specify Document Type *
            </label>
            <input
              id="customDocumentType"
              type="text"
              value={customDocumentType}
              onChange={(e) => handleCustomDocumentTypeChange(e.target.value)}
              placeholder="Enter document type (e.g., Police Report, Affidavit)"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
            <p className="text-sm text-gray-600 mt-1">
              Please specify what type of document you're uploading
            </p>
          </div>
        )}
      </div>

      {/* File Upload Component */}
      <div>
        <OmDocumentUpload
          ref={fileInputRef}
          onFileSelect={handleFileUpload}
          disabled={isLoading || !selectedDocumentType || (selectedDocumentType === 'OTHER' && !customDocumentType.trim())}
          acceptedFileTypes=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          maxFileSize={10485760} // 10MB
          multiple={true}
        />
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Uploaded Documents ({uploadedFiles.length}/10)</h3>
          
          {/* Required Documents */}
          {uploadedFiles.some(f => f.isRequired) && (
            <div className="space-y-2">
              <h4 className="font-medium text-green-700">Required Documents:</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {uploadedFiles
                  .filter(f => f.isRequired)
                  .map((file, index) => {
                    // Handle both 'type' (from backend) and 'documentType' (from upload)
                    const docType = file.documentType || file.type;
                    return (
                    <div key={`required-${file.id || index}`} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{file.filename || file.name}</p>
                        <p className="text-xs text-green-600 font-medium">
                          {getDocumentTypeLabel(docType)}
                        </p>
                        {file.size && (
                          <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(file.id || index)}
                        disabled={isLoading}
                        className="ml-4 text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  );
                  })}
              </div>
            </div>
          )}

          {/* Other Documents */}
          {uploadedFiles.some(f => !f.isRequired) && (
            <div className="space-y-2">
              <h4 className="font-medium text-blue-700">Other Documents:</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {uploadedFiles
                  .filter(f => !f.isRequired)
                  .map((file, index) => {
                    // Handle both 'type' (from backend) and 'documentType' (from upload)
                    const docType = file.documentType || file.type;
                    return (
                    <div key={`other-${file.id || index}`} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{file.filename || file.name}</p>
                        <p className="text-xs text-blue-600 font-medium">
                          {docType === 'OTHER' ? file.customDocumentType || 'Other Document' : getDocumentTypeLabel(docType)}
                          {file.customDocumentType && docType === 'OTHER' && ` (${file.customDocumentType})`}
                        </p>
                        {file.size && (
                          <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(file.id || index)}
                        disabled={isLoading}
                        className="ml-4 text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {(errors.documentType || validationErrors.documentType) && (
        <div className="text-red-600 text-sm">
          {errors.documentType || validationErrors.documentType}
        </div>
      )}
    </div>
  );
};

ModalDocumentUpload.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  validationErrors: PropTypes.object,
  uploadedFiles: PropTypes.array,
  isLoading: PropTypes.bool,
  onFileUpload: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  schema: PropTypes.object,
  showError: PropTypes.func,
  onUploadSuccess: PropTypes.func
};

export default ModalDocumentUpload;